import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { MediaKind, UploadMode, UploadTarget } from '../api/types'

export type QueueStatus = 'queued' | 'preparing' | 'uploading' | 'done' | 'failed' | 'cancelled'

/** Een geslaagd part van een multipart-upload; de ETag hoort bij het afronden. */
export interface UploadedPart {
  partNumber: number
  eTag: string
}

/**
 * Wat de server over deze upload heeft uitgedeeld, minus de handtekeningen.
 *
 * De presigned URLs zelf staan er bewust niet in: ze leven dertig minuten en
 * een hervatte upload moet ze toch verversen. Wat hier staat is wat níét
 * verloopt — welk media-item het is, of het een multipart is, en zo ja welke.
 */
export interface UploadPlan {
  mode: UploadMode
  expiresAtUtc: string

  /** Alleen bij Single, en alleen zolang `expiresAtUtc` nog niet voorbij is. */
  original?: UploadTarget
  web?: UploadTarget

  /** Alleen bij Multipart. Deze overleeft het verlopen van de handtekeningen. */
  uploadId?: string
  partSizeBytes?: number
  partCount?: number
}

/**
 * Eén item in de wachtrij, precies zoals het in IndexedDB staat.
 *
 * De originele `File` gaat als blob mee de database in. Dat is de hele reden
 * dat een gesloten tab niets verliest: zonder de bytes zou een hervatte upload
 * de gast opnieuw om het bestand moeten vragen, en dan is de wachtrij een
 * lijstje in plaats van een wachtrij.
 */
export interface QueueItem {
  /** Lokaal id; bestaat al voordat de server iets van dit item weet. */
  id: string

  slug: string
  guestId: string
  challengeId: string | null

  fileName: string
  contentType: string
  sizeBytes: number
  kind: MediaKind

  original: Blob

  /** De canvas-export. Ontbreekt tot de voorbereiding gelukt is. */
  web?: Blob

  /** Miniatuur als data-URL, zodat de wachtrij ook na een herstart beeld heeft. */
  poster?: string

  width?: number
  height?: number
  durationSeconds?: number | null

  status: QueueStatus

  /** 0 tot 1, over origineel plus webversie samen. */
  progress: number

  attempts: number

  /** Epoch-ms; vóór dit moment wordt er niet opnieuw geprobeerd. */
  nextAttemptAt: number

  error?: string

  /** Het id dat de server uitdeelde. Blijft staan tussen pogingen door. */
  mediaItemId?: string

  plan?: UploadPlan

  /**
   * De parts die al in de opslag staan, met hun ETag. Na élk geslaagd part
   * weggeschreven — zonder dat begint een hervatte upload van een video van
   * 500 MB alsnog bij nul en was het hele multipart-verhaal voor niets.
   */
  uploadedParts?: UploadedPart[]

  /** De webversie staat er al; hoeft niet nog eens. */
  webUploaded?: boolean

  createdAt: number
}

interface ConfettiDb extends DBSchema {
  uploads: {
    key: string
    value: QueueItem
    indexes: { 'by-slug': string }
  }
}

const databaseName = 'confetti'
const databaseVersion = 1

let handle: Promise<IDBPDatabase<ConfettiDb>> | null = null

export function db(): Promise<IDBPDatabase<ConfettiDb>> {
  handle ??= openDB<ConfettiDb>(databaseName, databaseVersion, {
    upgrade(database) {
      const store = database.createObjectStore('uploads', { keyPath: 'id' })
      store.createIndex('by-slug', 'slug')
    },
  })

  return handle
}

export async function putItem(item: QueueItem): Promise<void> {
  await (await db()).put('uploads', item)
}

export async function deleteItem(id: string): Promise<void> {
  await (await db()).delete('uploads', id)
}

export async function listItems(slug: string): Promise<QueueItem[]> {
  const items = await (await db()).getAllFromIndex('uploads', 'by-slug', slug)

  return items.sort((a, b) => a.createdAt - b.createdAt)
}
